import { sendChatMessage, sendMessageFeedback } from '../services/sessionService';

const generateMessageId = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const extractAndRemoveToolJson = (content, toolMap) => {
  const toolJsonRegex = /\{[^{}]*"tool_name"\s*:\s*"[^"]+"\s*[^{}]*\}/g;
  let cleanedContent = content;
  const matches = [...content.matchAll(toolJsonRegex)];
  
  for (const match of matches) {
    try {
      const parsed = JSON.parse(match[0]);
      if (parsed && parsed.tool_name) {
        const toolName = parsed.tool_name;
        
        if (!toolMap.has(toolName)) {
          toolMap.set(toolName, {
            tool_name: toolName,
            description: parsed.description || '',
            start_time: null,
            end_time: null,
            status: 'calling'
          });
        }
        
        const tool = toolMap.get(toolName);
        
        if (parsed.start_time) {
          tool.start_time = parsed.start_time;
          tool.status = 'in_progress';
        }
        if (parsed.description && !tool.description) {
          tool.description = parsed.description;
        }
        if (parsed.end_time) {
          tool.end_time = parsed.end_time;
          tool.status = 'completed';
          tool.completed = true;
        }
        
        toolMap.set(toolName, tool);
        cleanedContent = cleanedContent.replace(match[0], '');
      }
    } catch (e) {
    }
  }
  
  return cleanedContent;
};


export const useMessageHandling = () => {
  const handleSendMessage = async (
    sessionIdRef,
    createNewSession,
    userMessage,
    model,
    audioFile,
    knowledge_base,
    searchEnabled,
    setMessages,
    setIsLoading,
    isCurrentModelThinkingModel,
    scrollToBottom,
    inputRef,
    setInput,
    tempFileNames = [],
    abortController = null,
    uploadPendingFiles = null
  ) => {
    if (!userMessage && !audioFile) return;
    
    const messageToAdd = {
      id: generateMessageId(),
      text: userMessage || (audioFile ? `[Audio: ${audioFile.name}]` : ''),
      sender: 'user',
      hasAudio: !!audioFile,
      audioFile: audioFile ? URL.createObjectURL(audioFile) : null
    };
    
    setMessages(prev => [...prev, messageToAdd]);
    setIsLoading(true);

    setInput('');

    try {
      if (!sessionIdRef.current) {
        await createNewSession(userMessage);
      }

      let uploadedFileNames = tempFileNames;
      console.log('[DEBUG] handleSendMessage - Initial temp file names:', tempFileNames);
      console.log('[DEBUG] handleSendMessage - Session ID:', sessionIdRef.current);
      
      if (uploadPendingFiles && sessionIdRef.current) {
        console.log('[DEBUG] Uploading pending files with session:', sessionIdRef.current);
        try {
          const newlyUploadedFiles = await uploadPendingFiles(sessionIdRef.current);
          console.log('[DEBUG] Newly uploaded files:', newlyUploadedFiles);
          if (newlyUploadedFiles && newlyUploadedFiles.length > 0) {
            uploadedFileNames = [...tempFileNames, ...newlyUploadedFiles];
          }
        } catch (uploadError) {
          console.error('Failed to upload pending files:', uploadError);
          throw new Error('Failed to upload attached files. Please try again.');
        }
      }

      console.log('[DEBUG] Final uploaded file names to send:', uploadedFileNames);

      const response = await sendChatMessage(
        sessionIdRef.current, 
        userMessage, 
        model, 
        audioFile, 
        knowledge_base, 
        searchEnabled, 
        uploadedFileNames,
        abortController?.signal
      );
      if (!response.body) {
        throw new Error('No response body');
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let isThinkingModel = isCurrentModelThinkingModel();
      let isThinking = isThinkingModel;
      let thinkingContent = '';
      let responseContent = '';
      let messageId = null;
      let toolUse = [];
      const toolMap = new Map();
      const aiMessageId = generateMessageId();
      
      setMessages(prevMessages => [...prevMessages, { 
        id: aiMessageId,
        text: '', 
        sender: 'ai', 
        isThinking,
        thinkingContent: '',
        responseContent: '',
        messageId: null
      }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
      
        const chunk = decoder.decode(value);
        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let previousLineWasEmpty = false;

        for (const line of lines) {
          if (line.startsWith('data: metadata')) {
            try {
              const metadataStr = line.slice(14);
              const metadata = JSON.parse(metadataStr);
              if (metadata.message_id) {
                messageId = metadata.message_id;
              }
            } catch (e) {
              console.error('Error parsing metadata:', e);
            }
            continue;
          }
          
          if (line.startsWith('data: ')) {
            let content = line.slice(6);
            const originalContent = content;

            let isToolUseJson = false;
            try {
              const parsedContent = JSON.parse(content);
              if (parsedContent && typeof parsedContent === 'object') {
                const hasToolFields = parsedContent.tool_name || 
                                     parsedContent.start_time || 
                                     parsedContent.end_time ||
                                     parsedContent.function ||
                                     parsedContent.status ||
                                     (parsedContent.type === 'tool_call');
                if (hasToolFields) {
                  isToolUseJson = true;
                  if (parsedContent.function && parsedContent.function.name) {
                    const toolName = parsedContent.function.name;
                    const existingToolIndex = toolUse.findIndex(t => 
                      t.tool_name === toolName
                    );
                    if (existingToolIndex >= 0) {
                      toolUse[existingToolIndex] = {
                        ...toolUse[existingToolIndex],
                        start_time: parsedContent.start_time || toolUse[existingToolIndex].start_time,
                        end_time: parsedContent.end_time || toolUse[existingToolIndex].end_time,
                        status: parsedContent.status || toolUse[existingToolIndex].status,
                        completed: parsedContent.completed || toolUse[existingToolIndex].completed,
                        description: parsedContent.function.arguments?.user_description || 
                                     parsedContent.function.arguments?.query || 
                                     toolUse[existingToolIndex].description || ''
                      };
                    } else {
                      toolUse.push({
                        tool_name: toolName,
                        description: parsedContent.function.arguments?.user_description || 
                                     parsedContent.function.arguments?.query || 
                                     '',
                        start_time: parsedContent.start_time,
                        end_time: parsedContent.end_time,
                        status: parsedContent.status,
                        completed: parsedContent.completed
                      });
                    }
                  } else {
                    const existingToolIndex = toolUse.findIndex(t => 
                      t.tool_name === parsedContent.tool_name
                    );
                    if (existingToolIndex >= 0) {
                      toolUse[existingToolIndex] = {
                        ...toolUse[existingToolIndex],
                        start_time: parsedContent.start_time || toolUse[existingToolIndex].start_time,
                        end_time: parsedContent.end_time || toolUse[existingToolIndex].end_time,
                        status: parsedContent.status || toolUse[existingToolIndex].status,
                        completed: parsedContent.completed || toolUse[existingToolIndex].completed,
                        description: parsedContent.description || toolUse[existingToolIndex].description
                      };
                    } else {
                      toolUse.push(parsedContent);
                    }
                  }
                }
              }
            } catch (e) {
            }
            
            if (!isToolUseJson && content) {
              let cleanedContent = extractAndRemoveToolJson(content, toolMap);
              toolUse = Array.from(toolMap.values());
              
              if (isThinking) {
                thinkingContent += cleanedContent;

                if (cleanedContent.includes('</think>')) {
                  isThinking = false;
                  const parts = thinkingContent.split('</think>');
                  thinkingContent = parts[0];
                  if (parts.length > 1) {
                    responseContent += parts[1];
                  }
                }
              } else {
                responseContent += cleanedContent;
              }
            }

            previousLineWasEmpty = false;
          } else if (line === '') {
            if (previousLineWasEmpty) {
              if (isThinking) {
                thinkingContent += '\n';
              } else {
                responseContent += '\n';
              }
              previousLineWasEmpty = false;
            } else {
              previousLineWasEmpty = true;
            }
          }
        }

        setMessages(prevMessages => {
          const newMessages = [...prevMessages];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage?.sender === 'ai') {
            const rawText = isThinkingModel 
              ? (thinkingContent + (responseContent ? '</think>' + responseContent : ''))
              : responseContent;
            
            const updatedToolUse = [...toolUse];
              
            return newMessages.map((msg, i) =>
              i === newMessages.length - 1
                ? {
                    ...msg,
                    text: rawText,
                    isThinking,
                    thinkingContent,
                    responseContent,
                    messageId: messageId || msg.messageId,
                    toolUse: updatedToolUse
                  }
                : msg
            );
          }
          return newMessages;
        });
      }
      
      const finalToolUse = toolUse.map(tool => {
        const endTime = tool.end_time;
        const isDuration = endTime && typeof endTime === 'string' && endTime.match(/^\d+\.?\d*s$/);
        const hasResponseContent = responseContent && responseContent.length > 0;
        const hasSubstantialContent = responseContent && responseContent.length > 100;
        
        if (hasResponseContent || hasSubstantialContent) {
          if (isDuration) {
            return { ...tool, completed: true, status: 'completed' };
          }
          if (tool.start_time && !tool.end_time) {
            return { ...tool, completed: true, status: 'completed' };
          }
          if (tool.status === 'calling' || tool.status === 'in_progress') {
            return { ...tool, completed: true, status: 'completed' };
          }
        }
        return tool;
      });

      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage?.sender === 'ai') {
          let displayText = isThinkingModel
            ? (thinkingContent + (responseContent ? '</think>' + responseContent : ''))
            : responseContent;

          return newMessages.map((msg, i) => {
            if (i === newMessages.length - 1) {
              const finalMessage = {
                ...msg,
                text: displayText,
                isThinking,
                thinkingContent,
                responseContent,
                messageId: messageId || msg.messageId,
                toolUse: finalToolUse
              };


              return finalMessage;
            }
            return msg;
          });
        }
        return newMessages;
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sending message:', error);
        setMessages(prevMessages => [
          ...prevMessages,
          {
            id: generateMessageId(),
            text: 'Sorry, there was an error processing your request.',
            sender: 'ai',
          },
        ]);
      }
    } finally {
      setIsLoading(false);
      scrollToBottom();
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleFeedback = async (messageId, isGood, feedbackGiven, setFeedbackGiven) => {
    if (!messageId || feedbackGiven[messageId]) return;
    
    try {
      await sendMessageFeedback(messageId, isGood);
      setFeedbackGiven(prev => ({
        ...prev,
        [messageId]: isGood ? 'positive' : 'negative'
      }));
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleCopyText = async (text, messageId, model, setCopyFeedback) => {
    try {
      let textToCopy = text;
      
      if (model.includes('DeepSeek') && text.includes('</think>')) {
        textToCopy = text.split('</think>')[1];
      }
      
      await navigator.clipboard.writeText(textToCopy);
      
      setCopyFeedback(prev => ({
        ...prev,
        [messageId || Date.now()]: true
      }));
      
      setTimeout(() => {
        setCopyFeedback(prev => ({
          ...prev,
          [messageId || Date.now()]: false
        }));
      }, 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  return {
    handleSendMessage,
    handleFeedback,
    handleCopyText
  };
};
